import { useEffect, useState } from 'react';
import { API_URLS } from '../../apiUrl';
import { Pagination } from '@mui/material';
import { toast, ToastContainer } from 'react-toastify';
import axiosInstance from '../../components/axiosInstance';
import CustomSelect from '../../components/CustomSelect';

const SurveyorListInAdmin = () => {
  const [tableData, setTableData] = useState<any>();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);
  const [loading, setLoading] = useState(false);
  const [inputData, setInputData] = useState({ page: 1, limit: 100 });
  const [totalItems, setTotalItems] = useState(0);

  const [Municipality, SetMunicipality] = useState<MunicipalityType[]>([]);
  const [selectedmunici, setselectedmunici] = useState<string>("");

  const filteredData = tableData?.filter((item: any) => {
    const statusLabel = item.status === 1 ? 'active' : item.status === 2 ? 'inactive' : '';
  
    return (
      item.survyer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.municipality_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.borough_names.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.ward_no.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.street_names.toLowerCase().includes(searchQuery.toLowerCase()) ||
      statusLabel.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData?.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedmunici) return;
      setLoading(true);
      try {
        const response = await axiosInstance.post(`${API_URLS.ListSurveyor}`, {
          page: inputData.page,
          limit: inputData.limit,
          municipality_id: selectedmunici
        });

        if (response.data && response.data.data) {
          setTableData(response.data.data.data);
          setTotalItems(response.data.data.total);
        }
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message
        );
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [inputData.page, selectedmunici]);

  const handlePageChange = (event: any, value: number) => {
    setInputData((prevData) => ({
      ...prevData,
      page: value,
    }));
  };


  const [animatingItems, setAnimatingItems] = useState<{ [key: number]: boolean }>({});

  const toggleStatus = async (id: number, currentStatus: number) => {
    setAnimatingItems((prev) => ({ ...prev, [id]: true }));
    const newStatus = currentStatus === 1 ? 2 : 1;

    setTableData((prevData: any) =>
      prevData.map((item: any) =>
        item.surveyer_id === id ? { ...item, status: newStatus } : item
      )
    );

    try {
      const response = await axiosInstance.post(`${API_URLS.SurveyorStatus}`, {
        surveyor_id: id,
        status: newStatus
      });
      if (response.data.message == "surveyor status updated successfully") {
        toast.success('Status updated successfully!');
      } else {
        toast.error('Failed to update status.');
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message
      );
      console.error('Error updating status:', err);
    } finally {
      setTimeout(() => {
        setAnimatingItems((prev) => ({ ...prev, [id]: false }));
      }, 300);
    }
  };

interface MunicipalityType {
  primary_id: number;
  municipality_name: string;
}

interface SelectOptionType {
  value: string;
  label: string;
}

useEffect(() => {
  axiosInstance
    .post(API_URLS.MunicipalityList, {
      page: 1,
      limit: 100,
    })
    .then((res) => {
      const raw = res.data?.data;

      const list: MunicipalityType[] = Array.isArray(raw)
        ? raw
        : raw?.rows ?? raw?.data ?? [];

      SetMunicipality(list);
    })
    .catch((err: any) => {
      toast.error(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message
      );

      console.error("Municipality fetch error:", err);
    });
}, []);

const municipalityOptions: SelectOptionType[] = Municipality.map((c) => ({
  value: String(c.primary_id),
  label: c.municipality_name,
}));

  return (
    <div className="container mx-auto">
      <ToastContainer />
      <div className="flex gap-5 mb-4">
        <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Surveyor List</h1>
        <div className="flex justify-end md:w-4/12 w-full ml-auto">
          <input
            type="text"
            placeholder="Search..."
            className="border px-3 py-2 rounded-lg w-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-900 dark:bg-gray-800"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="flex gap-5 mb-4">
        <div className="w-[30%]">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Select Municipality *</label>
          <CustomSelect
            value={selectedmunici}
            onChange={(e: any) => setselectedmunici(e.target.value)}
            options={municipalityOptions}
          />
          {!selectedmunici && (
            <p className="text-red-500 text-sm mt-1">Please select municipality</p>
          )}
        </div>
      </div>

      {!selectedmunici ? (
        <div className="overflow-x-auto bg-white rounded-lg shadow-lg border border-gray-200 dark:border-gray-800">
          <table className="min-w-full table-auto">
            <thead className="bg-gray-100 border-b dark:border-gray-600 dark:bg-gray-700">
              <tr>
                <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Surveyer Name</th>
                <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Municipality</th>
                <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Borough Name</th>
                <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Ward No.</th>
                <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Street Names</th>
                <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={6} className="py-3 px-6 text-center dark:bg-gray-800">No results found</td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : loading ? (
        <div className="overflow-x-auto bg-white rounded-lg shadow-lg border border-gray-200 dark:border-gray-800">
          <table className="min-w-full table-auto">
            <thead className="bg-gray-100 border-b dark:border-gray-600 dark:bg-gray-700">
              <tr>
                <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Surveyer Name</th>
                <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Municipality</th>
                <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Borough Name</th>
                <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Ward No.</th>
                <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Street Names</th>
                <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={6} className="py-3 px-6 text-center dark:bg-gray-800">Loading...</td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto bg-white rounded-lg shadow-lg border border-gray-200 dark:border-gray-800">
            <table className="min-w-full table-auto">
              <thead className="bg-gray-100 border-b dark:border-gray-600 dark:bg-gray-700">
                <tr>
                  <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Surveyer Name</th>
                  <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Municipality</th>
                  <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Borough Name</th>
                  <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Ward No.</th>
                  <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Street Names</th>
                  <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Status</th>
                </tr>
              </thead>
              <tbody>
                {totalItems < 1 ? (
                  <tr>
                    <td colSpan={6} className="py-3 px-6 text-center dark:bg-gray-800">No results found</td>
                  </tr>
                ) : (
                  currentItems?.map((item: any, index: any) => (
                    <tr key={index} className="border-b dark:border-gray-700 hover:bg-gray-50">
                      <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">{item.survyer_name}</td>
                      <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">{item.municipality_name}</td>
                      <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">{item.borough_names}</td>
                      <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">{item.ward_no === null || item.ward_no === "" ? "-" : item.ward_no}</td>
                      <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">{item.street_names === null || item.street_names === "" ? "-" : item.street_names}</td>
                      <td className="py-3 px-6 text-sm text-center">
                        <button
                          onClick={() => toggleStatus(item.surveyer_id, item.status)}
                          className={`inline-block px-3 py-1 text-xs font-medium rounded-full border ${item.status === 1 ? 'text-green-500 border-green-200 bg-green-100' : 'text-red-500 border-red-200 bg-red-100'} ${animatingItems[item.type_primary_id] ? 'status-change' : ''}`}
                        >
                          {item.status === 1 ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalItems < 1 ? null : (
            <div className="flex justify-center mt-4">
              <Pagination
                count={totalPages}
                page={inputData.page}
                onChange={handlePageChange}
                color="primary"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SurveyorListInAdmin;